import { useCurrentUser } from "@/features/auth/hooks";
import { holds } from "@/features/auth/permissions";
import { useApiMutation, useApiQuery } from "@/lib/query";
import type { UserDetail, UserPage, UserRow, UserStatus } from "./types";

export const PAGE_SIZE = 100;

/** What the caller may do here, from `/auth/me`. The server checks again on every call. */
export function useUserAdminAccess() {
  const permissions = useCurrentUser().data?.permissions ?? [];
  return { canRead: holds(permissions, "users:read"), canManage: holds(permissions, "users:manage") };
}

export const useUsers = (filters: { q: string; status: UserStatus | ""; page: number }) =>
  useApiQuery<UserPage>(
    "/admin/users",
    { ...filters, page_size: PAGE_SIZE },
    { placeholderData: (prev) => prev },
  );

export const useUser = (id: string | null) =>
  useApiQuery<UserDetail>(`/admin/users/${encodeURIComponent(id ?? "")}`, {}, { enabled: id !== null });

/** Turn an account off or back on; the list and the open person refetch. */
export const useSetUserActive = () =>
  useApiMutation<UserRow, { id: string; active: boolean }>(
    "post",
    ({ id, active }) => `/admin/users/${encodeURIComponent(id)}/${active ? "reactivate" : "deactivate"}`,
    { invalidate: ["/admin/users"] },
  );
